import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Button } from 'react-bootstrap';
import UnitGauge from './UnitGauge';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { getTeamIdFromURL } from '@/_helpers';
import { BsArrowRepeat } from 'react-icons/bs';
import { analyticsApi } from '@/_services/api/analytics';

const Component = () => {
    const teamId = getTeamIdFromURL();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSummary = useCallback(async () => {
        if (!teamId) {
            setError('Team ID not found');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await analyticsApi.getClientUnitSummaryByTeam(teamId);
            setData(result);
        } catch (err) {
            setError(err.message || 'Failed to load unit summary');
        } finally {
            setLoading(false);
        }
    }, [teamId]);

    useEffect(() => {
        // to load after 3 sec, because manager can switch to other tabs quickly and this may affect performance
        const timer = setTimeout(() => {
            fetchSummary();
        }, 3000);

        return () => clearTimeout(timer);
    }, [fetchSummary]);

    const renderContent = () => {
        if (error) {
            return (
                <Alert variant="warning" dismissible onClose={() => setError(null)}>
                    <strong>Error:</strong> {error}
                    <Button variant="link" onClick={fetchSummary} style={{ marginLeft: 10 }}>
                        Retry
                    </Button>
                </Alert>
            );
        }

        if (loading && !data) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '20px 0' }}>
                    <Spinner animation="border" size="sm" />
                    <span>Loading unit authorization summary…</span>
                </div>
            );
        }

        const clientUnitSummaries = data?.results || [];
        if (!clientUnitSummaries.length) {
            return (
                <div style={{ color: '#64748b', fontSize: 12, padding: '8px 0' }}>
                    No active unit authorizations found for this team.
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'flex-start',
                }}
            >
                {clientUnitSummaries.map((clientSummary) => (
                    <UnitGauge key={String(clientSummary.client)} clientSummary={clientSummary} />
                ))}
            </div>
        );
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                }}
            >
                <div>
                    <strong>Service Authorization Usage</strong>
                </div>
                {loading && data && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                        <Spinner animation="border" size="sm" />
                        <span style={{ marginLeft: '8px' }}>Refreshing data…</span>
                    </span>
                )}

                <Button variant="outline-primary" onClick={fetchSummary} disabled={loading}>
                    <BsArrowRepeat className={loading ? 'spin' : ''} />
                </Button>
            </div>

            {renderContent()}
        </div>
    );
};

const ServiceAuthorizationUsage = (props) => (
    <ComponentErrorBoundary>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default ServiceAuthorizationUsage;
